import blue from '@material-ui/core/colors/blue'
import lightBlue from '@material-ui/core/colors/lightBlue'
import orange from '@material-ui/core/colors/orange'
import isEmpty from "lodash/isEmpty"
import last from "lodash/last"
import { getGroupColor } from "modules/player/constants"
import { formatPlayer } from "modules/player/format"
import SecurityManager from 'modules/security/SecurityManager'
import { encodeTinyId } from "modules/tinyid"
import moment from "moment"
import convertDiscordMarkdownToHTML from "../../modules/html/formatDiscordContent"
import formatHtmlContent from "../../modules/html/formatHtmlContent"

//
// Mise en forme des tickets pour l'affichage.
//
// Ces fonctions ne modifient PLUS les objets reçus : elles renvoient de nouveaux objets. Les
// tickets viennent du store Redux, les muter revenait à modifier le cache des commandes sous les
// pieds de React (et empêchait toute mémoïsation fiable).
//
// Le formatage d'un message est coûteux (conversion markdown Discord -> HTML, détection des
// vidéos/images/liens). Il est donc mis en cache par message : tant que son contenu n'a pas
// changé, on renvoie le MÊME objet formaté, ce qui permet en prime à React.memo de sauter le
// rendu des messages inchangés.
//

const MESSAGE_CACHE_LIMIT = 3000

const messageCache = new Map()

/** Ce qui, s'il change, impose de reformater le message. */
function getMessageSignature(message, ticket) {
	return [
		message.id,
		message.content,
		message.deleted,
		message.anonymous,
		message.previousVersions ? message.previousVersions.length : 0,
		message.discordMessageId,
		// ⚠️ Sans les réactions dans la signature, le message formaté restait en cache et les
		// nouvelles réactions n'apparaissaient jamais : une seule semblait "prendre", les autres
		// étaient perdues, et celles du joueur n'arrivaient pas.
		(message.reactions || []).map(reaction => `${reaction.emoji}:${reaction.count}:${(reaction.identifiers || []).join('+')}`).join(','),
		message.authorPlayer ? message.authorPlayer.identifier : '',
		// ⚠️ Même piège que les réactions : sans la citation dans la signature, un message dont
		// le message cité vient d'être supprimé resterait affiché avec l'ancien aperçu.
		message.replyTo ? `${message.replyTo.id}:${message.replyTo.resolved}:${message.replyTo.deleted}` : '',
		ticket ? ticket.creatorDiscordAvatar : '',
	].join('|')
}

function readMessageCache(key, signature) {
	const cached = messageCache.get(key)
	return cached && cached.signature === signature ? cached.value : null
}

function writeMessageCache(key, signature, value) {
	// Cache borné : au-delà de la limite on repart de zéro plutôt que de laisser la mémoire filer
	// sur une session ouverte plusieurs heures.
	if (messageCache.size >= MESSAGE_CACHE_LIMIT) {
		messageCache.clear()
	}
	messageCache.set(key, { signature, value })
	return value
}

function getColor(ticket) {
	if (ticket.waitingResponse) {
		return orange[300]
	}

	if (ticket.fromStaff) {
		return blue[200]
	}

	return 'white'
}

function getMessageAuthorColor(message) {
	if (message.fromBot) {
		return "rgb(32, 102, 148)"
	}

	if (message.fromCreator) {
		return null
	}

	if (message.fromStaff) {
		if (message.authorPlayer && message.authorPlayer.group) {
			return getGroupColor(message.authorPlayer.group)
		}
		return lightBlue[700]
	}

	return null
}

function getStateColor(state) {
	switch (state) {
		case "CREATED":
		case "RUNNING":
			return blue[600]

		default:
			return ""
	}
}

function getStateLabel(state) {
	switch (state) {
		case "CREATED":
			return "Crée"
		case "RUNNING":
			return "En cours"
		case "CLOSED":
			return "Fermé"

		default:
			return state
	}
}

function formatDate(date) {
	const today = moment()
	const yesterday = moment().add(-1, 'days')

	if (today.isSame(date, "day")) {
		return `Aujourd'hui à ${date.format('HH:mm')}`
	}
	if (yesterday.isSame(date, "day")) {
		return `Hier à ${date.format('HH:mm')}`
	}
	return date.format('DD/MM/YYYY')
}

const urlRegex = /(https?:\/\/[^ ]*)/

/** Retire l'URL du contenu : elle est déjà rendue sous forme d'aperçu intégré. */
function removeUrlFromContent(content) {
	const match = content.match(urlRegex)
	if (!match) {
		return content.trim()
	}
	return content.split(match[1]).join("").trim()
}

//
// Détection du type de contenu d'un message
//

//
// Fragments identifiant la conversation d'ouverture du bot, pour la replier dans le fil.
//
// ⚠️ Les fragments « ancienne rédaction » sont CONSERVÉS : ils sont les seuls à correspondre aux
// tickets ouverts avant la refonte des textes du bot, et les retirer déplierait d'un coup tout
// l'historique. Toute réécriture d'un message d'intro dans l'API (modules/ticket/bot/texts.js)
// doit donc AJOUTER son nouveau fragment ici, jamais remplacer l'ancien.
//

const BOT_OPENING_FIRST_MESSAGES = [
	// Ancienne rédaction
	"Avant qu'un membre du staff puisse répondre à ta demande, nous t'invitons à choisir le type de demande que tu formules :",
	"Avant de commencer, j'ai trouvé plusieurs personnages reliés à ton compte Discord.",
	// Rédaction éphémère du 28/07/2026, conservée : quelques tickets ont pu être ouverts avec.
	"Je suis l'assistant du support.",
	// Rédaction éphémère du 28/07/2026
	"Pour orienter ta demande vers la bonne équipe",
	// Rédaction actuelle
	"Ta question a peut-être déjà sa réponse.",
	"J'ai trouvé plusieurs personnages reliés à ton compte Discord.",
]

const BOT_OPENING_LAST_MESSAGES = [
	// Ancienne rédaction
	"Avant qu'un membre de l'équipe ne réponde à ta demande, merci de préciser au maximum les détails de l'erreur que tu as constatée dans cette sanction.", // deban
	"Merci de préciser au maximum les détails de ta demande. Ce ticket n'a pas pour but de négocier un bannissement ou de demander un déban anticipé.", // deban
	"avant qu'une réponse ne te soit apportée, merci de préciser au maximum les détails de ta demande, et d'envoyer les vidéos en cas de constatation de bug ou de non respect du règlement.", // ticket
	"Il est obligatoire de fournir un dossier de mort RP en suivant le modèle suivant", // mortrp
	"Merci de nous envoyer un Google Doc contenant ta candidature", // candidature
	"il s'agit d'une demande de réinitialisation de ton personnage", // register
	// Rédaction éphémère du 28/07/2026
	"Merci de préciser au maximum les détails de l'erreur que tu as constatée dans cette sanction", // deban
	"Cette demande n'a pas pour but de négocier un bannissement ni d'obtenir un déban anticipé.", // deban
	// Rédaction actuelle
	"décris dans cette conversation l'erreur que tu as constatée", // deban
	"Ton compte n'apparaissant pas comme banni", // deban, joueur non banni ayant une question
	"En cas de bug constaté ou de non-respect du règlement, une vidéo est indispensable.", // ticket
	"Un dossier est obligatoire, en suivant ce modèle", // mortrp
	"Il s'agit donc d'une candidature staff.", // candidature
	"Il s'agit donc d'une suggestion d'amélioration pour le serveur.", // suggestion
	"Il s'agit donc d'une demande de réinitialisation de ton personnage.", // register
	// Parcours « dossier mort RP » : le bot demande le Google Doc, et la discussion d'ouverture
	// s'arrête là. Sans ces deux repères, le bloc ne se refermait jamais et absorbait TOUT ce
	// que le joueur écrivait ensuite — ses propres messages disparaissaient du fil.
	"Il s'agit donc d'une demande de mort RP visant un autre joueur.", // mortrp
	"Il s'agit donc d'une demande de réinitialisation de ton propre personnage.", // register
]

const TICKET_CLOSED_MESSAGE = "Nous te remercions d'avoir contacté le support Baylife. A bientôt !"
const TICKET_ASSIGNED_MESSAGE = "Un ou plusieurs membres du staff sont maintenant assignés à ta demande. Merci de patienter, une réponse te sera apportée très bientôt."
const SUPPORT_BOT_DISCORD_TAG = "<@735253273208160358>"

function containsAny(content, fragments) {
	return fragments.some(fragment => content.includes(fragment))
}

/**
 * Analyse le contenu brut d'un message et renvoie les champs d'affichage correspondants
 * (contenu final, résumé pour les notifications, drapeaux de rendu).
 */
function formatMessageContent(rawContent) {
	const content = rawContent || ""
	const trimmedContent = content.trim()

	const base = {
		content,
		resume: "[inconnu]",
		textHasRawContent: false,
		textHasVideo: false,
		textHasImage: false,
		textHasSound: false,
		textHasLink: false,
		textHasYoutubeVideo: false,
	}

	if (content.startsWith(TICKET_CLOSED_MESSAGE)) {
		return { ...base, content: "> Fermeture du ticket", resume: "> Fermeture du ticket" }
	}

	if (content === TICKET_ASSIGNED_MESSAGE) {
		return { ...base, content: "> Assignation de ticket", resume: "> Assignation de ticket" }
	}

	if (trimmedContent.startsWith("https://tenor.com/view/")) {
		const match = content.match(/(https?:\/\/tenor.com.*-(\d.*))/)
		return {
			...base,
			isTenorGif: true,
			tenorGifEmbedurl: 'https://tenor.com/embed/',
			directUrl: match && match.length >= 3 ? match[1] : null,
			tenorGifId: match && match.length >= 3 ? match[2] : null,
			resume: "[gif]",
			content: removeUrlFromContent(trimmedContent),
		}
	}

	if (trimmedContent.includes("https://clips.twitch.tv")) {
		return {
			...base,
			isTwitchClip: true,
			directUrl: trimmedContent,
			clipSlug: trimmedContent.split("https://clips.twitch.tv/").join(""),
			content: removeUrlFromContent(trimmedContent),
			resume: "[clip Twitch]",
		}
	}

	if (trimmedContent.includes("https://streamable.com/")) {
		const match = trimmedContent.match(/(https:\/\/streamable.com\/(\w*))/)
		if (match && match.length >= 3) {
			return {
				...base,
				isStreamable: true,
				directUrl: match[1],
				clipSlug: match[2],
				content: removeUrlFromContent(trimmedContent),
				resume: "[clip Streamable]",
			}
		}
	}

	if (content.includes("https://docs.google.com/document/")) {
		const googleDocuments = []
		let remainingContent = content

		Array.from(content.matchAll(/(https:\/\/docs\.google\.com\/document\/d\/([a-zA-Z0-9-_]*)\/([\w?=&-]*))/g)).forEach(match => {
			remainingContent = remainingContent
				.replace(`${match[0]}\n\n`, "")
				.replace(`${match[0]}\n`, "")
				.replace(match[0], "")
			googleDocuments.push({
				googleDocumentUrl: match[0],
				googleDocumentId: match[2],
				googleDocPreviewUrl: `https://lh3.google.com/u/0/d/${match[2]}=w200-h150-p-k-nu-iv1`,
			})
		})

		return {
			...base,
			hasGoogleDocument: true,
			googleDocuments,
			content: remainingContent,
			resume: "[Google document]",
		}
	}

	if (/<@.*>$/.test(content)) { // mention Discord
		if (content === SUPPORT_BOT_DISCORD_TAG) {
			return { ...base, isDiscordTag: true, content: "@SupportBaylife", resume: "[PING] @SupportBaylife" }
		}
		return { ...base, isDiscordTag: true, content: `[PING]: ${content}`, resume: "[PING]" }
	}

	const htmlContent = formatHtmlContent(convertDiscordMarkdownToHTML(content))

	let resume = "[inconnu]"
	if (htmlContent.textHasRawContent) {
		resume = htmlContent.content.substr(0, 100)
	} else if (htmlContent.textHasVideo) {
		resume = "[video]"
	} else if (htmlContent.textHasImage) {
		resume = "[image]"
	} else if (htmlContent.textHasSound) {
		resume = "[audio]"
	} else if (htmlContent.textHasLink) {
		resume = "[link]"
	} else if (htmlContent.textHasYoutubeVideo) {
		resume = "[youtube]"
	}

	return {
		...base,
		content: htmlContent.content,
		resume,
		textHasVideo: htmlContent.textHasVideo,
		textHasImage: htmlContent.textHasImage,
		textHasSound: htmlContent.textHasSound,
		textHasLink: htmlContent.textHasLink,
		textHasYoutubeVideo: htmlContent.textHasYoutubeVideo,
		textHasRawContent: htmlContent.textHasRawContent,
	}
}

/**
 * Formate un message du feed.
 * ticket / mePlayer sont facultatifs : les notifications websocket n'ont pas le ticket.
 */
export function formatFeedMessage(message, { ticket, mePlayer } = {}) {
	const signature = getMessageSignature(message, ticket)
	const cached = readMessageCache(message.id, signature)
	if (cached) {
		return cached
	}

	const content = message.content || ""
	const isOpeningFirstMessage = containsAny(content, BOT_OPENING_FIRST_MESSAGES)
	const isOpeningLastMessage = containsAny(content, BOT_OPENING_LAST_MESSAGES)

	const createdAt = moment(message.createdAt)
	const previousVersions = (message.previousVersions || []).map(previousVersion => ({
		...previousVersion,
		editedAt: moment(previousVersion.editedTimestamp),
	}))

	const fromBot = message.fromType === "BOT"
	const fromStaff = message.fromType === "STAFF"
	const fromCreator = message.fromType === "CREATOR"

	// Un compte Discord sans photo renvoie une URL se terminant par null.png : on l'ignore pour
	// afficher l'avatar de repli.
	let creatorDiscordAvatar = fromCreator ? (ticket && ticket.creatorDiscordAvatar) : message.creatorDiscordAvatar
	if (creatorDiscordAvatar && creatorDiscordAvatar.endsWith("null.png")) {
		creatorDiscordAvatar = null
	}

	const ticketId = message.ticketId !== undefined && message.ticketId !== null
		? message.ticketId
		: (ticket && ticket.id)
	const myIdentifier = mePlayer && mePlayer.identifier

	// `mine` est calculé ICI, pas par l'API : la diffusion websocket d'un ticket part à tous les
	// membres du staff avec la même charge utile, un champ calculé côté serveur serait donc faux
	// pour tout le monde sauf un — et l'affichage optimiste ne se réconciliait jamais.
	const reactions = (message.reactions || []).map(reaction => ({
		...reaction,
		mine: !!myIdentifier && (reaction.identifiers || []).indexOf(myIdentifier) !== -1,
	}))

	const meIsCreator = message.author === myIdentifier

	// ⚠️ Un message SUPPRIMÉ ne se modifie ni ne se supprime : il n'a plus de contenu à éditer,
	// et le supprimer une seconde fois n'a pas de sens. Le contrôle est posé ici plutôt que sur
	// chacun des deux endroits qui affichent ces actions — la barre de survol et le menu
	// contextuel — pour qu'aucun ne puisse l'oublier.
	const canEdit = !message.deleted
		&& fromStaff
		&& (meIsCreator || SecurityManager.hasPermission("api.ticket.canUpdateAllMessages"))

	const formatted = {
		...message,
		...formatMessageContent(content),

		// Contenu D'ORIGINE, avant conversion en HTML. Indispensable à l'édition : sans lui, on
		// renvoyait à l'API le HTML généré à la place du texte tapé par le membre du staff.
		rawContent: content,

		// Les notifications websocket appellent formatFeedMessage sans ticket : l'id vient alors
		// du message lui-même.
		ticketDisplayId: ticketId !== undefined && ticketId !== null ? encodeTinyId(ticketId) : null,

		isMessageOpeningTicketDiscussionWithBotFirstMessage: isOpeningFirstMessage,
		isMessageOpeningTicketDiscussionWithBotLastMessage: isOpeningLastMessage,
		isMessageOpeningTicketDiscussionWithBot: isOpeningFirstMessage || isOpeningLastMessage,

		fromBot,
		fromStaff,
		fromCreator,

		createdAt,
		createdAtFormatted: formatDate(createdAt),
		previousVersions,
		reactions,
		hasEdit: !isEmpty(previousVersions),

		hasNoPlayer: !message.authorPlayer,
		authorDisplayName: (message.authorPlayer && message.authorPlayer.fullName) || (ticket && ticket.creatorDiscordName) || "Inconnu",
		creatorDiscordAvatar,

		meIsCreator,
		canEdit,
		canDelete: canEdit,

		// Un message du staff sans ID Discord n'a jamais été accepté par Discord (api2
		// injoignable, bot bloqué, compte supprimé). Il est enregistré chez nous, mais le joueur
		// ne l'a PAS reçu — il faut le dire, sinon rien ne distingue les deux cas.
		isUndelivered: fromStaff && !message.discordMessageId,

		// Citation (fonction « Répondre » de Discord). Voir formatReplyTo.
		replyTo: formatReplyTo(message.replyTo, ticket),
	}

	formatted.authorColor = getMessageAuthorColor(formatted)

	return writeMessageCache(message.id, signature, formatted)
}

/**
 * Met en forme le message cité par une réponse.
 *
 * Discord n'en montre qu'UNE ligne, tronquée : c'est un rappel de contexte, pas une relecture.
 * On retire donc les sauts de ligne — sans quoi une citation de dix lignes repousserait le
 * message lui-même hors de l'écran — et le balisage Markdown, illisible réduit à une ligne.
 *
 * Les trois états possibles sont ceux de Discord :
 *   - message retrouvé            -> auteur + extrait
 *   - message supprimé depuis     -> mention explicite
 *   - message introuvable chez nous -> mention explicite également, plutôt que rien : savoir
 *     qu'il s'agit d'une réponse reste une information, même sans son objet.
 */
function formatReplyTo(replyTo, ticket) {
	if (!replyTo) {
		return null
	}

	if (!replyTo.resolved) {
		return { resolved: false, text: "Message d'origine introuvable" }
	}

	if (replyTo.deleted) {
		return {
			resolved: true,
			deleted: true,
			id: replyTo.id,
			discordMessageId: replyTo.discordMessageId,
			authorDisplayName: (replyTo.authorPlayer && replyTo.authorPlayer.fullName)
				|| (ticket && ticket.creatorDiscordName) || "Inconnu",
			text: "Message supprimé",
		}
	}

	const preview = String(replyTo.content || '')
		.replace(/\s*\n+\s*/g, ' ')
		.replace(/[*_~`>|]/g, '')
		.trim()

	return {
		resolved: true,
		deleted: false,
		id: replyTo.id,
		discordMessageId: replyTo.discordMessageId,
		authorDisplayName: (replyTo.authorPlayer && replyTo.authorPlayer.fullName)
			|| (ticket && ticket.creatorDiscordName) || "Inconnu",
		fromBot: replyTo.fromType === 'BOT',
		text: preview || "Pièce jointe",
	}
}

/**
 * Regroupe les messages consécutifs d'un même auteur et masque la discussion d'ouverture
 * avec le bot. Renvoie un nouveau tableau de nouveaux objets.
 */
function decorateFeed(feed) {
	const firstMessage = feed[0]
	let openingFirstMessageFound = false

	const decorated = feed.map((message, index) => ({ ...message }))

	decorated.forEach((message, index) => {
		const previousMessage = index !== 0 ? decorated[index - 1] : null
		const nextMessage = decorated[index + 1]

		message.isNewDate = !!previousMessage && !previousMessage.createdAt.isSame(message.createdAt, "day")

		if (index === 0 || message.isNewDate) {
			message.isFirstMessageOfMultiple = true
		} else {
			message.isFirstMessageOfMultiple = previousMessage.author !== message.author
				|| previousMessage.fromType !== message.fromType
				|| !!previousMessage.anonymous !== !!message.anonymous
		}

		message.isLastMessageOfMultiple = !nextMessage || nextMessage.author !== message.author

		// Le bot renvoie parfois deux fois son message d'accueil : on ne garde que le premier.
		if (message.isMessageOpeningTicketDiscussionWithBotFirstMessage) {
			if (openingFirstMessageFound) {
				message.isMessageOpeningTicketDiscussionWithBotFirstMessage = false
			} else {
				openingFirstMessageFound = true
			}
		}

		// Tant que la discussion d'ouverture n'est pas terminée, les messages en font partie.
		if (previousMessage
			&& previousMessage.isMessageOpeningTicketDiscussionWithBot
			&& !previousMessage.isMessageOpeningTicketDiscussionWithBotLastMessage) {
			message.isMessageOpeningTicketDiscussionWithBot = true
		}

		// Beaucoup de joueurs recopient leur premier message après avoir répondu au bot : on le
		// masque en le rattachant à la discussion d'ouverture.
		if (previousMessage
			&& previousMessage.isMessageOpeningTicketDiscussionWithBotLastMessage
			&& firstMessage
			&& firstMessage.content === message.content
			&& !firstMessage.deleted) {
			previousMessage.isMessageOpeningTicketDiscussionWithBotLastMessage = false
			message.isMessageOpeningTicketDiscussionWithBot = true
			message.isMessageOpeningTicketDiscussionWithBotLastMessage = true
		}
	})

	return decorated
}

/**
 * Pseudo Discord, discriminant compris seulement s'il en subsiste un.
 *
 * Discord a supprimé les discriminants en 2023 : les comptes migrés portent « 0 », qui signifie
 * « aucun ». L'accoler produisait « maxmonroe#0 » — un vestige que plus personne ne sait lire.
 */
function formatDiscordTag(nom, discriminant) {
	const pseudo = nom || 'Inconnu'
	const suffixe = String(discriminant || '')
	return !suffixe || suffixe === '0' ? pseudo : `${pseudo}#${suffixe}`
}

function formatTicket(ticket, mePlayer) {
	if (!ticket) {
		return ticket
	}

	const feed = decorateFeed((ticket.feed || []).map(message => formatFeedMessage(message, { ticket, mePlayer })))

	let creatorDiscordAvatar = ticket.creatorDiscordAvatar
	if (creatorDiscordAvatar && creatorDiscordAvatar.endsWith("null.png")) {
		creatorDiscordAvatar = null
	}

	const lastFeedMessage = last(feed)
	// Sur les listes, l'API ne renvoie pas le feed : on se rabat alors sur lastAnswer, sinon la
	// couleur "en attente de réponse" ne s'affichait jamais dans la colonne de gauche.
	const waitingResponse = lastFeedMessage
		? !!lastFeedMessage.fromCreator
		: moment(ticket.lastAnswer).isBefore(moment().subtract(1, 'day'))

	const authorDisplayName = (ticket.creator && ticket.creator.fullName) || ticket.creatorDiscordName || "Inconnu"

	const formatted = {
		...ticket,
		feed,
		creatorDiscordAvatar,
		waitingResponse,

		createdAt: moment(ticket.createdAt),
		displayId: encodeTinyId(ticket.id),

		isTypeMortRp: ticket.type === "mortrp",
		isTypeRegister: ticket.type === "register",

		hasNoPlayer: !ticket.creator,
		authorDisplayName,
		creatorDiscordNameWithDiscriminator: formatDiscordTag(ticket.creatorDiscordName, ticket.creatorDiscordDiscriminator),

		isStateClosed: ticket.state === 'CLOSED',
		player: formatPlayer(ticket.player),
		stateLabel: getStateLabel(ticket.state),
		stateColor: getStateColor(ticket.state),

		meIsAssigned: (ticket.assignedMemberPlayers || []).some(member => member.player.identifier === (mePlayer && mePlayer.identifier)),
		meIsWatching: (ticket.watcherPlayers || []).some(member => member.player.identifier === (mePlayer && mePlayer.identifier)),

		hasCustomName: !!ticket.customName,
		ticketDisplayName: ticket.customName || authorDisplayName,
		unreadMessages: ticket.unreadMessages || 0,

		isWaitingForResponse: moment(ticket.lastAnswer).isBefore(moment().subtract(24, 'hours')),
		isWaitingForResponseHard: moment(ticket.lastAnswer).isBefore(moment().subtract(72, 'hours')),
	}

	formatted.color = getColor(formatted)

	return formatted
}

/**
 * Formate un message en cours d'envoi pour qu'il soit rendu par le MÊME composant qu'un message
 * réel — donc avec exactement la même mise en page.
 *
 * Les drapeaux de regroupement (en-tête avec avatar et pseudo, séparateur de date) sont calculés
 * par rapport au dernier message du fil, comme le ferait decorateFeed. Sans eux, l'en-tête
 * apparaissait au moment de la confirmation et faisait sauter la ligne.
 */
export function formatOptimisticMessage(rawMessage, { ticket, mePlayer, previousMessage } = {}) {
	const formatted = formatFeedMessage(rawMessage, { ticket, mePlayer })

	const isNewDate = !!previousMessage && !previousMessage.createdAt.isSame(formatted.createdAt, "day")
	// ⚠️ `anonymous` arrive de la base en 0/1 sur un message réel, et en true/false sur un
	// message optimiste : une comparaison stricte les jugeait différents, l'en-tête (avatar +
	// pseudo) apparaissait donc le temps de l'envoi puis disparaissait à la confirmation.
	const isFirstMessageOfMultiple = !previousMessage
		|| isNewDate
		|| previousMessage.author !== formatted.author
		|| previousMessage.fromType !== formatted.fromType
		|| !!previousMessage.anonymous !== !!formatted.anonymous

	return {
		...formatted,
		isNewDate,
		isFirstMessageOfMultiple,
		// Un message en cours d'envoi est toujours le dernier du fil.
		isLastMessageOfMultiple: true,
	}
}

export function formatTickets(tickets, mePlayer) {
	return (tickets || []).map(ticket => formatTicket(ticket, mePlayer))
}

export default formatTicket
